import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { getAdminBanners, clearErrors, deleteBanner, createBanner } from '../../actions/bannerAction';
import { DELETE_BANNER_RESET, NEW_BANNER_RESET } from '../../constants/bannerConstants';
import MetaData from '../Layouts/MetaData';
import Loader from '../Layouts/Loader';
import { Modal, Box, Button, TextField, Card, CardMedia, CardActions, CardContent, Typography } from '@mui/material';

const Banners = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const { loading, error, banners } = useSelector((state) => state.banners);
    const { isDeleted, error: deleteError } = useSelector((state) => state.deleteBanner);
    const { success, error: newBannerError } = useSelector((state) => state.newBanner);

    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [images, setImages] = useState([]);
    const [imagesPreview, setImagesPreview] = useState([]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (deleteError) {
            enqueueSnackbar(deleteError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (newBannerError) {
            enqueueSnackbar(newBannerError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isDeleted) {
            enqueueSnackbar("Banner Deleted Successfully", { variant: "success" });
            dispatch({ type: DELETE_BANNER_RESET });
        }
        if (success) {
            enqueueSnackbar("Banner Created Successfully", { variant: "success" });
            dispatch({ type: NEW_BANNER_RESET });
            handleClose();
            setName("");
            setImages([]);
            setImagesPreview([]);
        }
        dispatch(getAdminBanners());
    }, [dispatch, error, deleteError, newBannerError, isDeleted, success, enqueueSnackbar]);

    const deleteBannerHandler = (id) => {
        dispatch(deleteBanner(id));
    }

    const handleBannerImageChange = (e) => {
        const files = Array.from(e.target.files);

        setImages([]);
        setImagesPreview([]);

        files.forEach((file) => {
            const reader = new FileReader();

            reader.onload = () => {
                if (reader.readyState === 2) {
                    setImagesPreview((oldImages) => [...oldImages, reader.result]);
                }
            };
            reader.readAsDataURL(file);
            setImages((oldImages) => [...oldImages, file]);
        });
    };

    const newBannerSubmitHandler = (e) => {
        e.preventDefault();

        if (images.length <= 0) {
            enqueueSnackbar("Add Banner Image", { variant: "warning" });
            return;
        }

        const formData = new FormData();
        formData.append("name", name);

        images.forEach((image) => {
            formData.append("images", image);
        });

        dispatch(createBanner(formData));
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    return (
        <>
            <MetaData title="Admin: Banners | Flipkart" />

            <div className="flex-1 p-4">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-semibold">Banners</h1>
                    <Button variant="contained" onClick={handleOpen}>Add Banner</Button>
                </div>

                {loading ? <Loader /> : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {banners && banners.map((banner) => (
                            <Card key={banner._id}>
                                <CardMedia
                                    component="img"
                                    height="140"
                                    image={banner.url}
                                    alt={banner.name}
                                />
                                <CardContent>
                                    <Typography gutterBottom variant="h5" component="div">
                                        {banner.name}
                                    </Typography>
                                </CardContent>
                                <CardActions>
                                    <Button size="small" color="error" onClick={() => deleteBannerHandler(banner._id)}>Delete</Button>
                                </CardActions>
                            </Card>
                        ))}
                    </div>
                )}

                <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <Box sx={style}>
                        <Typography id="modal-modal-title" variant="h6" component="h2">
                            New Banner
                        </Typography>
                        <form onSubmit={newBannerSubmitHandler} className="flex flex-col gap-4 mt-4">
                            <TextField
                                label="Name"
                                variant="outlined"
                                fullWidth
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                            <Button
                                variant="contained"
                                component="label"
                            >
                                Upload File
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleBannerImageChange}
                                    multiple
                                />
                            </Button>
                            <div className="flex gap-4 overflow-x-auto">
                                {imagesPreview.map((image, i) => (
                                    <img src={image} alt="Banner Preview" className="w-32 h-32 object-contain" key={i} />
                                ))}
                            </div>
                            <Button type="submit" variant="contained" disabled={loading}>Create Banner</Button>
                        </form>
                    </Box>
                </Modal>
            </div>
        </>
    );
};

export default Banners;